import React from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Stack } from '@mantine/core'

import { AttendeeTranscriptOrderCard } from './AttendeeTranscriptOrderCard'
import {
  clearAttendeeTranscriptOrders,
  undoOrderConfirmationIfCompleted,
} from './closingOrders'
import { OrderConfirmationRow } from './OrderConfirmationRow'

import { BooleanSelect } from '@/components/common/BooleanSelect'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { NoWrapText } from '@/components/Event/NewScript/components/common/NoWrapText'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { ActionText } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionText'
import { ActionTitle } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionTitle'
import { DBFContact } from '@/constants'
import { Event, EventScriptDataObject, ScriptAttendee } from '@/types'
import { isAttorney } from '@/utils/validations'

type TranscriptOrderFormValues = {
  order_transcript?: boolean | null
  attendees: ScriptAttendee[]
}

type TranscriptOrderCollectionContentProps = {
  event: Event
  state: EventScriptDataObject
  isActionCompleted: (key: string) => boolean
  undoAction: (key: string) => void
}

export const TranscriptOrderCollectionContent: React.FC<
  TranscriptOrderCollectionContentProps
> = ({ event, state, isActionCompleted, undoAction }) => {
  const methods = useFormContext<TranscriptOrderFormValues>()
  const { fields, update } = useFieldArray({
    control: methods.control,
    name: 'attendees',
  })
  const watchedAttendees = methods.watch('attendees')
  const invalidateOrderConfirmation = () =>
    undoOrderConfirmationIfCompleted(isActionCompleted, undoAction)

  return (
    <ActionContainer outer>
      <Stack>
        <ActionText mono>
          Are there any transcript orders at this time?
        </ActionText>
        <ActionContainer>
          <Stack>
            <BooleanSelect
              value={methods.watch('order_transcript') ?? null}
              onChange={value => {
                methods.setValue('order_transcript', value)
                if (!value) {
                  methods.setValue(
                    'attendees',
                    clearAttendeeTranscriptOrders(
                      methods.getValues('attendees'),
                    ),
                  )
                }
                methods.trigger(['order_transcript', 'attendees'])
                invalidateOrderConfirmation()
              }}
              placeholder="Select option"
              label="Are there any orders?"
              style={{ width: 200 }}
              error={!!methods.formState.errors.order_transcript?.message}
            />
            {methods.formState.errors.order_transcript !== undefined && (
              <FormErrors
                errors={methods.formState.errors.order_transcript.message}
              />
            )}
          </Stack>
        </ActionContainer>
        {state.order_transcript && (
          <ActionContainer>
            <ActionText mono>
              Would you like the standard turnaround of{' '}
              <NoWrapText>7–10</NoWrapText> business days? We also offer{' '}
              <NoWrapText>3–5</NoWrapText> business days or 1 business day.
            </ActionText>
          </ActionContainer>
        )}
        {state.order_transcript &&
          fields
            .map((field, index) => ({
              field,
              index,
              attendee: watchedAttendees?.[index] ?? field,
            }))
            .filter(
              ({ attendee }) => isAttorney(attendee.role) && attendee.present,
            )
            .map(({ field, index, attendee }) => (
              <AttendeeTranscriptOrderCard
                key={field.id}
                attendee={attendee}
                event={event}
                namePrefix={`attendees.${index}`}
                allAttendees={watchedAttendees ?? []}
                onChange={value => {
                  update(index, value)
                  methods.trigger(['order_transcript', `attendees.${index}`])
                  invalidateOrderConfirmation()
                }}
              />
            ))}
        <ActionContainer>
          <ActionTitle>Reminder</ActionTitle>
          <ActionText>
            {DBFContact.NAME}’s standard turnaround time is 7–10 business days.
            Expedited turnaround times of 3–5 business days or 1 business day
            are available for an additional cost. All transcripts are delivered
            electronically. Transcript orders can also be placed after the event
            on {DBFContact.NAME}’s website, using a link that will be emailed to
            the attorneys. Cost inquiries and other questions can be sent to{' '}
            {DBFContact.SUPPORT_EMAIL}. Exhibits can be sent to{' '}
            {DBFContact.EXHIBITS_EMAIL}.
          </ActionText>
        </ActionContainer>
        {event.team.organization.name === 'Travelers' &&
        event.case.state === 'MN' ? (
          <ActionContainer>
            <ActionTitle>Hard Copy Requests</ActionTitle>
            <ActionText>
              Applicable for Travelers only. If a counsel requests a hard copy
              (in lieu of or in addition to the electronic transcript), please
              record the address and make a note in the Notes box to inform our
              support team of the request. If there are questions about
              logistics or billing, direct the counsel to{' '}
              {DBFContact.SUPPORT_EMAIL}.
            </ActionText>
          </ActionContainer>
        ) : null}
      </Stack>
    </ActionContainer>
  )
}

type TranscriptOrderConfirmationContentProps = {
  state: EventScriptDataObject
}

export const TranscriptOrderConfirmationContent: React.FC<
  TranscriptOrderConfirmationContentProps
> = ({ state }) => {
  const methods = useFormContext<TranscriptOrderFormValues>()
  const watchedAttendees = methods.watch('attendees')
  const orderRows = (state.order_transcript ? watchedAttendees : [])
    .map((attendee, index) => ({
      index,
      attendee,
    }))
    .filter(
      ({ attendee }) =>
        isAttorney(attendee.role) &&
        attendee.present &&
        attendee.transcript_order !== null,
    )

  return (
    <ActionContainer outer>
      <Stack>
        <ActionContainer>
          <ActionTitle>Reminder</ActionTitle>
          <ActionText>
            Spell out email addresses when confirming orders.
          </ActionText>
        </ActionContainer>
        <ActionText mono>
          Just to confirm the orders placed are as follows.
        </ActionText>
        {orderRows.length === 0 ? (
          <ActionText>No orders placed.</ActionText>
        ) : (
          orderRows.map(({ attendee, index }) => (
            <OrderConfirmationRow
              key={attendee.attendee_id}
              namePrefix={`attendees.${index}`}
            />
          ))
        )}
      </Stack>
    </ActionContainer>
  )
}
